import * as React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Option } from '@webapp/types/common/option';

import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';
import { rgba } from 'polished';
import { getWidth } from '@components/styles';
import { trackList } from '@webapp/store/tracks/selectors';

import AppState from '@webapp/store/state';

import DropdownCtrl from '../dropdown';
import MenuDropdownHeader from '../menu-dropdown/menu-dropdown-header';
import { getAssetTrackingId } from '@webapp/utils/assets/get-tracking-id';
import * as assetsSelectors from '@webapp/store/assets/selectors';

import Flex from '@components/layout/flex';
import Grid from '@components/basics/grid';
import Tooltip from '@components/basics/tooltip';
import DotsIcon from '@components/basics/icons/dots';
import CrossIcon from '@components/basics/icons/cross';
import { easeTransition } from '@components/framer';
import MotionOpacity from '@components/motion/motion-opacity';
import Typography from '@components/basics/typography';
import { Translate } from 'react-localize-redux';
import { AssetUpload } from '@webapp/types/assets/asset-upload';
import { ReplaceAsset } from '@webapp/store/assets/action-creators';
import { ToggleBottomMenu } from '@webapp/store/ui/action-creators';

interface TrackActionsMenuDropdownProps {
  className?: string;
  children?: React.ReactNode;
  menuContent?: Option[];
  label?: React.ReactNode;
  placeholder?: React.ReactNode;
  isSmall?: boolean;
  hasDottedIcon?: boolean;
  mobileTooltip?: boolean;
  active?: any;
  title: string;
  isMobile: boolean;
  onSelect?: (active: any) => void;
  hasWhiteBg?: boolean;
  onClose?: () => void;
  assets: AssetUpload[];
  trackId?: string;
  trackingId?: string;
  replaceAsset: (
    assetId: string,
    file: File,
    trackingId: string
  ) => ReplaceAsset;
  toggleBottomMenu: (state: boolean) => ToggleBottomMenu;
}

const mapStateToProps = (
  state: AppState,
  props: Pick<TrackActionsMenuDropdownProps, 'trackingId'>
) => {
  const tracks = trackList(state.tracks.tracks, state.release.release?.id);
  const assets: AssetUpload[] = [];
  tracks.forEach((track) => {
    const asset = state.assets.assets[track.id as string];
    if (asset) {
      assets.push(asset);
    }
  });
  return {
    assets,
    totalProgress: props.trackingId
      ? assetsSelectors.totalProgress(state.assets, props.trackingId)
      : 0,
    generalStatus: props.trackingId
      ? assetsSelectors.generalStatus(state.assets, props.trackingId)
      : undefined,
    errors: props.trackingId
      ? assetsSelectors.uploadErrors(state.assets, props.trackingId)
      : [],
    uploaded: props.trackingId
      ? assetsSelectors.getUploaded(state.assets, props.trackingId)
      : 0,
    total: props.trackingId
      ? assetsSelectors.getTotalUploads(state.assets, props.trackingId)
      : 0,
    isMobile: state.ui.mobile,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  replaceAsset: (assetId: string, file: File, trackingId: string) =>
    dispatch(new ReplaceAsset(assetId, 'audio', file, undefined, trackingId)),
  toggleBottomMenu: (state: boolean) => dispatch(new ToggleBottomMenu(state)),
});

export const TrackActionsMenuDropdown: React.FC<
  TrackActionsMenuDropdownProps
> = (props) => {
  const { onClose, toggleBottomMenu } = props;
  const [isOpen, setOpen] = React.useState(false);
  const [dropdown, setDropdown] = React.useState<boolean>(false);
  const [isSelected, setSelected] = React.useState(false);
  const hiddenFileInput = React.useRef<HTMLInputElement>(null);
  const variants = {
    initial: {
      bottom: '-100%',
    },
    animate: {
      bottom: 0,
      transition: { ...easeTransition },
    },
  };

  React.useEffect(() => {
    if (!isOpen) {
      onClose && onClose();
    }
  }, [onClose, isOpen]);

  React.useEffect(() => {
    toggleBottomMenu(dropdown);
  }, [dropdown, toggleBottomMenu]);

  const handleClick = (item: Option) => {
    return props.onSelect ? props.onSelect(item.value) : null;
  };

  const handleFileInputClick = () => {
    if (hiddenFileInput && hiddenFileInput.current) {
      hiddenFileInput.current.click();
    }
  };

  const validFile = (file: File): boolean => {
    const supportedExtensions = ['wav', 'mp3', 'aac', 'ogg', 'flac'];

    const path = file.name.split('.');
    const extension = `${path[path.length - 1]}`;

    if (
      !(
        supportedExtensions.includes(extension) ||
        file.type.startsWith('audio/')
      )
    ) {
      return false;
    }

    if (props.assets.length > 0) {
      const isRepeated = props.assets.find(
        (asset) => asset.filename === file.name && asset.size === file.size
      );
      if (!!isRepeated) {
        return false;
      }
    }
    return true;
  };

  const handleFile = (file: File, newTrackingId: string) => {
    if (props.trackId) {
      props.replaceAsset(props.trackId, file, newTrackingId);
    }
  };

  const handleFileInputChange = (event) => {
    const fileUploaded = event.target.files[0];
    if (validFile(fileUploaded)) {
      const newTrackingId = getAssetTrackingId();
      handleFile(fileUploaded, newTrackingId);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleText = (item: Option) => {
    return item.withTranslation ? (
      item.isRedText ? (
        <RedText weight="700">
          <Translate id={item.label} />
        </RedText>
      ) : (
        <Typography weight="700">
          <Translate id={item.label} />
        </Typography>
      )
    ) : item.value.includes('delete') ? (
      <RedText weight="700">{item.label}</RedText>
    ) : (
      <Typography weight="700">{item.label}</Typography>
    );
  };

  return (
    <>
      <input
        type="file"
        ref={hiddenFileInput}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        accept={props.isMobile ? '*' : 'audio/*'}
      />
      {!props.isMobile ? (
        <DropdownCtrl
          className={props.className}
          customContent
          header={
            <MenuDropdownHeader
              isOpen={isOpen}
              label={props.label}
              placeholder={props.placeholder}
              onClick={() => setOpen(!isOpen)}
              hasDottedIcon={props.hasDottedIcon}
            />
          }
          onClose={handleClose}
        >
          {isOpen ? (
            <Tooltip
              className="menu-content-wrapper"
              top="0px"
              right="-30px"
              minWidth="auto"
              noMotion
              isOpen={isOpen}
            >
              {props.menuContent
                ? props.menuContent.map((item, i) => (
                    <MenuDropdownItem
                      className={`menu-item ${
                        props.active === item.value ? 'selected' : ''
                      }`}
                      key={`menu-dropdown-item-${i}`}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        handleClick(item);
                        setOpen(!isOpen);
                        setSelected(!isSelected);
                      }}
                    >
                      {item.value === 'replace-file' ? (
                        <div onClick={handleFileInputClick}>
                          {handleText(item)}
                        </div>
                      ) : (
                        handleText(item)
                      )}
                    </MenuDropdownItem>
                  ))
                : props.children}
            </Tooltip>
          ) : null}
        </DropdownCtrl>
      ) : (
        <>
          <StyledDotsIcon onClick={() => setDropdown(true)} />
          <AnimatePresence exitBeforeEnter>
            {dropdown && (
              <FixedBottomMenuWrapper>
                <span className="backdrop" onClick={() => setDropdown(false)} />
                <div className="bottom-menu">
                  <motion.div
                    className="bottom-menu-items"
                    variants={variants}
                    animate={dropdown ? 'animate' : 'initial'}
                    initial="initial"
                  >
                    <Title
                      alignItems="center"
                      justifyContent="space-between"
                      gap={20}
                    >
                      <Typography type="big-title" customFont="semi-bold">
                        {props.title.length > 20
                          ? `${props.title.slice(0, 20)}...`
                          : props.title}
                      </Typography>
                      <CrossIcon
                        className="circle"
                        darkGrey={true}
                        onClick={() => setDropdown(false)}
                      />
                    </Title>
                    <Grid gap={10}>
                      {props.menuContent?.map((item) => {
                        return item.value === 'replace-file' ? (
                          <div onClick={handleFileInputClick}>
                            {handleText(item)}
                          </div>
                        ) : (
                          handleText(item)
                        );
                      })}
                    </Grid>
                  </motion.div>
                </div>
              </FixedBottomMenuWrapper>
            )}
          </AnimatePresence>
        </>
      )}
    </>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(TrackActionsMenuDropdown)`
  position: relative;

  .label {
    margin-right: 12px;
  }

  .label,
  .icon-arrow,
  .menu-item {
    cursor: pointer;
    pointer-events: auto;
  }
  ${(props) =>
    props.hasWhiteBg
      ? css`
          .menu-content-wrapper,
          .menu-items-container {
            .menu-content {
              background-color: ${({ theme }) => theme.colors.background};
            }
          }
        `
      : null}
  ${(props) =>
    props.isSmall
      ? css`
          ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
            .menu-content-wrapper,
            .menu-items-container {
              width: ${getWidth(3.5)};
            }
          }
        `
      : css`
          ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
            .menu-content-wrapper,
            .menu-items-container {
              width: max-content;
            }
          }
        `}

  ${(props) =>
    props.mobileTooltip
      ? css`
          .menu-content-wrapper {
            top: 0;
            ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
              top: 100%;
            }
          }
        `
      : null}

  .menu-item {
    opacity: 1;
    transition: opacity
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      margin-bottom: 10px;
    }

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      opacity: 0.75;
    }
  }
`);

const MenuDropdownItem = styled(Flex)`
  justify-content: flex-start;
  align-items: center;
  text-align: left;

  .menu-dropdown-item-icon {
    margin-right: 8px;
    height: 24px;
    width: 24px;
  }
`;

const RedText = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
`;

const StyledDotsIcon = styled(DotsIcon)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const FixedBottomMenuWrapper = styled(MotionOpacity)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${({ theme }) => theme.zIndices.dialogs};

  .backdrop {
    background: ${({ theme }) => rgba(theme.colors.black, 0.3)};
    position: absolute;
    inset: 0;
  }

  .bottom-menu {
    height: 100%;
    z-index: ${({ theme }) => theme.zIndices.dialogs};

    .bottom-menu-items {
      position: absolute;
      width: 100%;
      padding: 2rem;
      background: ${({ theme }) => theme.colors.differentBackground};
      border-radius: 20px 20px 0px 0px;
      z-index: ${({ theme }) => theme.zIndices.dialogs};
      min-height: 200px;
      min-height: max-content;

      span {
        font-weight: 700;
      }

      .accent {
        padding-top: 20px;
        color: ${({ theme }) => theme.colors.accent};
      }
    }
  }
`;

const Title = styled(Flex)`
  width: 100%;
  padding-bottom: 20px;
`;
